import { AccessSegment, HomeShortsFeed, HomeShortsFeeds } from './types';

/**
 * The first active feed (array order) where
 *   (levels is empty OR includes the level) AND (access is empty OR includes the access)
 * wins, skipping feeds with no usable short (none of their ids in `usableIds`, i.e. all
 * deleted or inactive). Returns null when none match, meaning the app uses `data.default`.
 * The backend mirrors this rule.
 */
export const resolveFeed = (
  data: Pick<HomeShortsFeeds, 'feeds'>,
  level: number,
  access: AccessSegment,
  usableIds: Set<string>,
): HomeShortsFeed | null =>
  data.feeds.find(
    (feed) =>
      feed.isActive &&
      (feed.levels.length === 0 || feed.levels.includes(level)) &&
      (feed.access.length === 0 || feed.access.includes(access)) &&
      feed.shortIds.some((id) => usableIds.has(id)),
  ) ?? null;

// True when every value matched by `inner` is also matched by `outer` (empty = any).
const isCovered = <T>(inner: T[], outer: T[]) =>
  outer.length === 0 || (inner.length > 0 && inner.every((value) => outer.includes(value)));

/**
 * Maps a feed id to the name of an earlier active feed that targets everything it
 * targets, so it can never win. Inactive feeds are not reported.
 */
export const getShadowedFeeds = (feeds: HomeShortsFeed[]): Map<string, string> => {
  const shadowed = new Map<string, string>();
  feeds.forEach((feed, index) => {
    if (!feed.isActive) {
      return;
    }
    const blocker = feeds
      .slice(0, index)
      .find((above) => above.isActive && isCovered(feed.levels, above.levels) && isCovered(feed.access, above.access));
    if (blocker) {
      shadowed.set(feed.id, blocker.name);
    }
  });
  return shadowed;
};
